import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import Carousel from "./Carousel";
import { isPresent, hasItems } from "./helpers";
import { useTheme } from "../../context/ThemeContext";

function PlanCard({ plan }) {
  return (
    <div
      className={`relative h-full p-8 pt-12 flex flex-col ${
        plan.most_chosen
          ? "bg-[var(--dvx-surface)] border-2 border-[var(--dvx-primary)]"
          : "bg-[var(--dvx-surface)] border border-[var(--dvx-border)]"
      }`}
    >
      {/* Most chosen badge */}
      {plan.most_chosen && (
        <div className="absolute top-0 left-0 right-0 flex justify-center -translate-y-1/2">
          <span className="bg-[var(--dvx-primary)] text-white text-xs px-4 py-1.5 font-oswald uppercase tracking-wider">
            Most Chosen
          </span>
        </div>
      )}

      {/* Plan name */}
      <h3 className="font-oswald text-2xl font-bold text-[var(--dvx-text)] uppercase">
        {plan.name || "Unnamed Plan"}
      </h3>

      {/* Description */}
      {isPresent(plan.description) && (
        <p className="mt-2 text-[var(--dvx-text-muted)] text-sm font-manrope">
          {plan.description}
        </p>
      )}

      {/* Price */}
      <div className="mt-5 flex items-baseline gap-1">
        {plan.amount ? (
          <>
            <span className="font-oswald text-lg text-[var(--dvx-text-muted)]">
              {plan.currency || "$"}
            </span>
            <span className="font-oswald font-bold text-4xl text-[var(--dvx-text)]">
              {parseFloat(plan.amount).toFixed(0)}
            </span>
            <span className="text-[var(--dvx-text-muted)] text-sm font-manrope">
              /mo
            </span>
          </>
        ) : (
          <span className="font-oswald font-bold text-sm text-[var(--dvx-text)]">
            Will be discussed based on the client’s timeline and goals.
          </span>
        )}
      </div>
      {/* Divider */}
      <div className="mt-6 border-t border-[var(--dvx-border)]" />

      {/* Features */}
      <ul className="mt-6 space-y-3 flex-1">
        {hasItems(plan.features) ? (
          plan.features.map((feature, i) => (
            <li key={i} className="flex gap-3">
              <Check
                size={16}
                className="text-[var(--dvx-primary)] mt-0.5 flex-shrink-0"
              />
              <span className="text-[var(--dvx-text-muted)] text-sm font-manrope">
                {feature}
              </span>
            </li>
          ))
        ) : (
          <li className="text-sm text-[var(--dvx-text-muted)]">
            No features listed.
          </li>
        )}
      </ul>

      {/* CTA */}
      <Button
        onClick={() => {
          if (isPresent(plan.cta_href)) window.location.href = plan.cta_href;
          else window.location.href = "/contact";
        }}
        className="w-full mt-8 py-4 rounded-none font-oswald uppercase tracking-widest text-sm bg-gradient-to-r from-[var(--dvx-primary)] to-[var(--dvx-primary-dark)] text-white hover:scale-105 transition-transform"
      >
        {plan.cta_text || "Apply"}
        <ArrowRight className="ml-2" size={16} />
      </Button>

      <p className="mt-3 text-xs text-[var(--dvx-text-muted)] text-center font-manrope">
        We love clients who can execute.
      </p>
    </div>
  );
}

export default function PlansPreview({ section, plans }) {
  if (!section || !hasItems(plans)) return null;
  const { theme } = useTheme();
  const [itemsPerPage, setItemsPerPage] = useState(2);

  useEffect(() => {
    let timeout;

    const updateItems = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        setItemsPerPage(window.innerWidth < 768 ? 1 : 3);
      }, 150);
    };

    updateItems();
    window.addEventListener("resize", updateItems);

    return () => window.removeEventListener("resize", updateItems);
  }, []);

  return (
    <section
      data-testid="plans-preview"
      id="pricing"
      className="relative py-5 md:py-5 transition-colors duration-300 scroll-mt-24 bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: "url('/images/pricing_bg.jpeg')" }}
    >
      {/* Overlay */}
      <div
        className={`absolute inset-0 transition-all duration-300 ${
          theme === "dark" ? "bg-black/60" : "bg-white/70"
        }`}
      ></div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          {isPresent(section.badge) && (
            <h3 className="text-[var(--dvx-primary)] font-oswald text-xl font-semibold uppercase tracking-[0.20.em]">
              {section.badge}
            </h3>
          )}
          {isPresent(section.title) && (
            <h2 className="mt-4 font-oswald text-4xl md:text-4xl lg:text-4xl font-bold text-[var(--dvx-text)] uppercase tracking-tight">
              {section.title}
            </h2>
          )}
          {/* {isPresent(section.description) && (
            <p className="mt-4 text-[var(--dvx-text-muted)] font-manrope text-lg max-w-2xl mx-auto">
              {section.description}
            </p>
          )} */}
        </motion.div>

        {/* pt-6 gives space for the "Most Chosen" badge that pops above cards */}
        <div className="pt-6">
          <Carousel
            items={plans}
            itemsPerPage={itemsPerPage}
            autoplayMs={5000}
            pauseOnHover={true}
            viewportClassName="pt-4"
            renderItem={(plan) => <PlanCard key={plan.id} plan={plan} />}
          />
        </div>
      </div>
    </section>
  );
}
